/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import swal from 'sweetalert';

import CasinoCRMApi from '../../../../casino/Api/CRM/CasinoCRMApi';
import { CasinoGameDeviceType } from '../../../../casino/Api/Models/CasinoGame';
import { CasinoGameCategorySortingType } from '../../../../casino/Api/Models/CasinoGameCategory';
import DeleteButton from '../../../elements/DeleteButton';
import LoadingButton from '../../../elements/common/LoadingButton';
import FormGroupNumber from '../../../elements/formElements/FormGroupNumber';
import GameSearchInput from '../../elements/GameSearchInput';
import { API_CASINO_GAME_CATEGORIES_INSERT_GAME, API_CASINO_GAME_CATEGORIES_REMOVE_GAME } from '../Helpers/CrmApiUrls';
import { CRM_PERMISSION } from '../Helpers/CrmPermissions';
import { CASINO_GAME_CATEGORY } from '../Helpers/CrmUrls';
import { replaceUrlParams } from '../Helpers/UrlHelper';

import { GetCasinoGameCategoryResponse } from 'casino/Api/CRM/Responses/CasinoGameCategoryResponse';
import { CasinoGameCategorySortedGameResponse } from 'casino/Api/CRM/Responses/CasinoGameCategorySortedGamesResponse';
import Search from 'crm/elements/Search';
import Card from 'crm/elements/common/Card';
import TableLoader from 'crm/elements/common/TableLoader';
import Paginator, { PaginatorProps } from 'crm/elements/paginator';
import useAutoPaginator from 'crm/hooks/useAutoPaginator';
import useNumberParams from 'crm/hooks/useNumberParams';

function GameCategoryShow() {
    const { id } = useNumberParams();
    const history = useHistory();
    const [isCustomSortingType, setIsCustomSortingType] = useState<boolean>(false);
    const [isMovingOrder, setIsMovingOrder] = useState<boolean>(false);
    const [gameCategory, setGameCategory] = useState<GetCasinoGameCategoryResponse>({
        id: 0,
        is_visible_on_homepage: false,
        is_all_games: false,
        is_live_only: false,
        is_filled_manually: false,
        allowed_types: [],
        sorting_type: CasinoGameCategorySortingType.DEFAULT,
        created_at: null,
        updated_at: null,
        casino_game_category_translations: [],
    });
    const {
        items: desktopGames,
        updateItems: updateDesktopGames,
        setItems: setDesktopGames,
        isLoading: isLoadingDesktopGames,
        auto: autoDesktopGames,
    } = useAutoPaginator<CasinoGameCategorySortedGameResponse>((page, params) => {
        return CasinoCRMApi.gameCategories.getPaginatedSortedGames(id, page, CasinoGameDeviceType.DESKTOP, { params });
    });

    const {
        items: mobileGames,
        updateItems: updateMobileGames,
        setItems: setMobileGames,
        isLoading: isLoadingMobileGames,
        auto: autoMobileGames,
    } = useAutoPaginator<CasinoGameCategorySortedGameResponse>((page, params) => {
        return CasinoCRMApi.gameCategories.getPaginatedSortedGames(id, page, CasinoGameDeviceType.MOBILE, { params });
    });

    async function getGameCategory() {
        await CasinoCRMApi.gameCategories
            .getById(id)
            .then((gameCategoryFromApi) => {
                setGameCategory(gameCategoryFromApi);
                setIsCustomSortingType(gameCategoryFromApi.sorting_type === CasinoGameCategorySortingType.CUSTOM);
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'Something went wrong...',
                    icon: 'error',
                }).then(() => {
                    history.push({ pathname: CASINO_GAME_CATEGORY });
                });
            });
    }

    const updateGameCategoryGameOrder = async (orderId, order) => {
        await CasinoCRMApi.gameCategoryGameOrder.updateOrder(orderId, order).catch(() => {
            swal({
                title: 'Error',
                text: 'Something went wrong...',
                icon: 'error',
            });
        });
    };

    const handleMoveOrder = async (gameId: number, deviceType: string) => {
        setIsMovingOrder(true);
        const games = deviceType === CasinoGameDeviceType.DESKTOP ? desktopGames : mobileGames;
        const gameToUpdate = games.find((game: CasinoGameCategorySortedGameResponse) => game.id === gameId);
        await updateGameCategoryGameOrder(gameToUpdate.pivot.id, gameToUpdate.pivot.order);
        if (deviceType === CasinoGameDeviceType.DESKTOP) {
            updateDesktopGames();
        } else {
            updateMobileGames();
        }
        setIsMovingOrder(false);
    };

    const handleChangeOrder = (event: React.ChangeEvent<HTMLInputElement>, gameId: number, deviceType: string) => {
        const games = deviceType === CasinoGameDeviceType.DESKTOP ? desktopGames : mobileGames;
        let newOrder = parseInt(event.target.value, 10);

        if (Number.isNaN(newOrder) || newOrder < 1) {
            newOrder = 1;
        }

        // 2^31 - 1, we can't handle bigger number
        if (newOrder > 2147483647) {
            newOrder = 2147483647;
        }

        const updatedGames = games.map((game) => {
            if (game.id === gameId) {
                return { ...game, pivot: { ...game.pivot, order: newOrder } };
            }
            return game;
        });
        const setGames = deviceType === CasinoGameDeviceType.DESKTOP ? setDesktopGames : setMobileGames;
        setGames(updatedGames);
    };

    const handleUpdateItems = async (game: CasinoGameCategorySortedGameResponse) => {
        if (game.is_desktop) {
            updateDesktopGames();
        }

        if (game.is_mobile) {
            updateMobileGames();
        }
    };

    useEffect(() => {
        getGameCategory();
    }, []);

    function generateTable(
        type: CasinoGameDeviceType,
        games: CasinoGameCategorySortedGameResponse[],
        isLoading: boolean,
        updateItems: (page?: number) => Promise<void>,
        paginator: PaginatorProps,
        search: {
            filterTable: (value: string) => void;
        }
    ) {
        return (
            <Card title={<h4 className="card-title text-capitalize font-weight-bold">{type} games</h4>}>
                <Search {...search} />
                <TableLoader isLoading={isLoading}>
                    <div className="d-flex flex-wrap">
                        {games && games.length > 0 ? (
                            games.map((game) => {
                                return (
                                    <div className="card" style={{ width: '20%', padding: '6px' }} key={game.id}>
                                        <img className="card-img-top" src={game.image} alt={game.name} />
                                        <div className="card-body">
                                            <h5>{game.creator_name}</h5>
                                            <h4 className="card-title" style={{ marginBottom: '25px' }}>
                                                {isCustomSortingType && `${game.pivot.order} - `} {game.name}
                                            </h4>
                                            {isCustomSortingType && (
                                                <FormGroupNumber
                                                    name={`game_category_game_order_${game.pivot.id}`}
                                                    value={game.pivot.order}
                                                    handleChange={(event) => handleChangeOrder(event, game.id, type)}
                                                    label="Order"
                                                    error={null}
                                                />
                                            )}
                                            <div className="d-flex justify-content-between">
                                                {isCustomSortingType && (
                                                    <LoadingButton
                                                        isLoading={isMovingOrder}
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => handleMoveOrder(game.id, type)}
                                                    >
                                                        Move
                                                    </LoadingButton>
                                                )}
                                                {gameCategory.is_all_games === false && (
                                                    <DeleteButton
                                                        url={replaceUrlParams(API_CASINO_GAME_CATEGORIES_REMOVE_GAME, {
                                                            categoryId: gameCategory.id,
                                                            gameId: game.id,
                                                        })}
                                                        action={() => handleUpdateItems(game)}
                                                        permissions={[CRM_PERMISSION.GAME_CATEGORY_DELETE_GAME]}
                                                        key="delete"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <span className="text-danger">Category does not have any games for {type} device.</span>
                        )}
                    </div>
                </TableLoader>
                <Paginator {...paginator} />
            </Card>
        );
    }

    return (
        <div>
            {gameCategory.is_all_games === false && (
                <GameSearchInput
                    action={() => {
                        updateDesktopGames();
                        updateMobileGames();
                    }}
                    allowedTypes={gameCategory.allowed_types}
                    liveOnly={gameCategory.is_live_only}
                    insertUrl={replaceUrlParams(API_CASINO_GAME_CATEGORIES_INSERT_GAME, { id })}
                />
            )}
            <div className="card">
                {generateTable(
                    CasinoGameDeviceType.DESKTOP,
                    desktopGames,
                    isLoadingDesktopGames,
                    updateDesktopGames,
                    autoDesktopGames.paginator,
                    autoDesktopGames.search
                )}
            </div>
            <div className="card">
                {generateTable(
                    CasinoGameDeviceType.MOBILE,
                    mobileGames,
                    isLoadingMobileGames,
                    updateMobileGames,
                    autoMobileGames.paginator,
                    autoMobileGames.search
                )}
            </div>
        </div>
    );
}

export default GameCategoryShow;
